import { useEffect } from "react";
import { tagOutboundHref } from "@/lib/outbound";

/**
 * Tags every outbound `<a>` click with `?utm_source=BeHot.Now` right before
 * the browser navigates, using capture-phase listeners so the rewrite runs
 * ahead of the default action. Covers left-click, middle-click (new tab),
 * right-click (copy link) and keyboard activation, and works for anchors
 * rendered after mount without any per-component wiring.
 */
export function useOutboundTagging() {
  useEffect(() => {
    function rewriteFor(el: EventTarget | null) {
      const a = (el as Element | null)?.closest?.("a") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href) return;
      const tagged = tagOutboundHref(href);
      if (tagged !== href) a.setAttribute("href", tagged);
    }

    function onPointerDown(e: Event) {
      rewriteFor(e.target);
    }
    function onClick(e: Event) {
      rewriteFor(e.target);
    }
    function onContextMenu(e: Event) {
      rewriteFor(e.target);
    }
    function onAuxClick(e: Event) {
      rewriteFor(e.target);
    }

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("contextmenu", onContextMenu, true);
    document.addEventListener("auxclick", onAuxClick, true);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("contextmenu", onContextMenu, true);
      document.removeEventListener("auxclick", onAuxClick, true);
    };
  }, []);
}
